/** @file Sampler for rgb and dark timelines: unpacks colour words into per-channel slot floats. */
import { color } from '@pose2d/math/color';
import { lerp } from '@pose2d/math/lerp';
import { POSE_LAYOUT as L } from '../../contracts/pose';
import type { Sample } from '../../contracts/sample';
import type { SkeletonData, TimelineData } from '../../skeletonData';
import { evalCurve } from '../../curve/evalCurve';
import { locateKey, type KeyCursor } from './locateKey';
import { sampleIndex } from './sampleIndex';

const RGB = [L.SLOT_R, L.SLOT_G, L.SLOT_B] as const;
const DARK = [L.SLOT_DARK_R, L.SLOT_DARK_G, L.SLOT_DARK_B] as const;
const cur: KeyCursor = { k0: 0, k1: 0, u: 0 };
const c0: [number, number, number, number] = [0, 0, 0, 0];
const c1: [number, number, number, number] = [0, 0, 0, 0];

type ColorTimeline = Extract<TimelineData, { k: 'rgb' | 'dark' }>;

/** Samples an rgb/dark timeline into three slot channels (0..1); the curve yields one normalized factor. */
export const colorHandler = {
  sample(tl: ColorTimeline, time: number, lastTime: number, out: Sample, data: SkeletonData): void {
    if (!locateKey(tl.times, time, cur)) return;
    const f = cur.k1 === cur.k0 ? 0 : evalCurve(tl.curves.kind, tl.curves.cp, cur.k0, 0, 1, cur.u);
    color.unpack(tl.values[cur.k0] ?? 0, c0);
    color.unpack(tl.values[cur.k1] ?? 0, c1);
    const offsets = tl.k === 'rgb' ? RGB : DARK;
    for (let c = 0; c < 3; c++) {
      const i = sampleIndex(data, 'slot', tl.slot, offsets[c] ?? 0);
      out.values[i] = lerp((c0[c] ?? 0) / 255, (c1[c] ?? 0) / 255, f);
      out.mask[i] = 1;
    }
  },
};
